import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { MarkerGlyph } from './StepCard';
import { STEP_SHORT, STEP_STEPPER, stepNumber, type Progress, type StepState, type StepStatus } from './steps';
import { markerTone, stepperStatusText, type MarkerTone } from './stepViewModel';

interface Props {
  states: StepState[];
  progress: Progress;
  /** Required keys missing, for the env step's warning line ("1 required key missing in production"). */
  envWarning?: string;
}

const MARKER_TONE_CLASS: Record<MarkerTone, string> = {
  done: 'border-succeeded/40 bg-succeeded/15 text-succeeded',
  attention: 'border-awaiting/40 bg-awaiting/15 text-awaiting',
  current: 'border-foreground bg-foreground text-background',
  idle: 'border-border text-subtle',
};

function Marker({ s, current }: { s: StepState; current: boolean }) {
  const tone = markerTone(s.status, current);
  return (
    <span aria-hidden className={cn('flex size-5 shrink-0 items-center justify-center rounded-full border font-mono text-[11px] font-semibold', MARKER_TONE_CLASS[tone])}>
      <MarkerGlyph tone={tone} n={stepNumber(s.id)} iconSize={13} />
    </span>
  );
}

const STATUS_TONE_CLASS: Partial<Record<StepStatus, string>> = { done: 'text-succeeded', attention: 'text-awaiting' };

/** The warning under the env step's row ("1 required key missing in production"). */
function EnvWarning({ s, text }: { s: StepState; text: string | undefined }) {
  if (s.id !== 'env' || s.status !== 'attention' || !text) return null;
  return <span className="mt-0.5 pl-[30px] font-mono text-[10px] leading-snug text-awaiting">{text}</span>;
}

/** One anchor per step: marker, number and label, status text, and (env) the missing-keys warning. */
function StepperRow({ s, current, envWarning }: { s: StepState; current: boolean; envWarning?: string }) {
  const blocked = s.status === 'blocked';
  return (
    <li>
      <a
        href={`#gs-step-${s.id}`}
        data-testid={`gs-stepper-${s.id}`}
        data-status={s.status}
        aria-current={current ? 'step' : undefined}
        className={cn(
          'flex min-h-9 flex-col justify-center rounded-md px-2 py-1.5 text-[13px] transition-colors duration-150 hover:bg-accent',
          current && 'bg-secondary font-medium text-foreground shadow-[inset_2px_0_0_var(--foreground)]',
          s.status === 'attention' && !current && 'bg-awaiting/5',
          blocked && 'text-subtle',
        )}
      >
        <span className="flex items-center gap-2.5">
          <Marker s={s} current={current} />
          <span className={cn('min-w-0 flex-1 truncate', blocked ? 'text-subtle' : 'text-foreground')}>
            {stepNumber(s.id)}. {STEP_STEPPER[s.id]}
          </span>
          <span className={cn('shrink-0 font-mono text-[10px]', current ? 'text-foreground' : (STATUS_TONE_CLASS[s.status] ?? 'text-subtle'))}>{stepperStatusText(s, current)}</span>
        </span>
        <EnvWarning s={s} text={envWarning} />
      </a>
    </li>
  );
}

/** Desktop: the sticky left stepper, one anchor per step, the current one emphasized, "Step N of 7" at the bottom. */
export function SetupStepper({ states, progress, envWarning }: Props) {
  const position = progress.next ? stepNumber(progress.next) : states.length;
  return (
    <nav aria-label="Setup steps" data-testid="gs-stepper" className="sticky top-20 flex flex-col gap-0.5 rounded-lg border bg-card p-3 shadow-card">
      <Kicker className="px-2 py-1 text-[11px] text-subtle">Setup steps</Kicker>
      <ol className="flex flex-col gap-0.5">
        {states.map((s) => (
          <StepperRow key={s.id} s={s} current={progress.next === s.id} envWarning={envWarning} />
        ))}
      </ol>
      <div className="mt-2 flex items-center justify-between border-t px-2 pt-2.5 text-[11px] text-subtle">
        <span>Status</span>
        <span className="font-mono text-muted-foreground">{progress.next ? `Step ${position} of ${states.length}` : 'All done'}</span>
      </div>
    </nav>
  );
}

/** Tablets: the same steps as one row of chips that scrolls sideways (never wraps). */
export function SetupStepperChips({ states, progress }: Props) {
  return (
    <nav aria-label="Setup steps" data-testid="gs-stepper-chips" className="-mx-1 overflow-x-auto px-1 pb-1">
      <ol className="flex w-max items-center gap-1.5 rounded-lg border bg-card p-2 shadow-card">
        {states.map((s, i) => {
          const current = progress.next === s.id;
          return (
            <li key={s.id} className="flex items-center gap-1.5">
              {i > 0 ? <Icon name="chevron_right" size={16} className="text-subtle" /> : null}
              <a
                href={`#gs-step-${s.id}`}
                data-testid={`gs-chip-${s.id}`}
                data-status={s.status}
                aria-current={current ? 'step' : undefined}
                className={cn(
                  'flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-[13px] transition-colors duration-150 hover:bg-accent',
                  current ? 'border-foreground/40 bg-secondary font-medium text-foreground' : s.status === 'attention' ? 'border-awaiting/30 text-foreground' : 'text-muted-foreground',
                )}
              >
                {s.status === 'done' ? <Icon name="check" size={14} className="text-succeeded" /> : s.status === 'attention' ? <span className="font-mono text-xs text-awaiting">!</span> : null}
                {stepNumber(s.id)}. {STEP_SHORT[s.id]}
                {current ? <span className="font-mono text-[10px] text-subtle">next</span> : null}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
